import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import './DocumentSearch.css';
import {
  Search,
  ArrowLeft,
  Sun,
  Moon,
  Menu,
  User,
  X,
  FileText,
  Download,
  ThumbsUp,
  ThumbsDown,
  RefreshCw,
  Send
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://webapp-python-nlng-bots-api-cuasbrg3h5cyewgm.westeurope-01.azurewebsites.net';

const DocumentSearch = ({ onBack, onNavigate, onSearch }) => { // Add onSearch prop
  // ============================================
  // STATE MANAGEMENT
  // ============================================
  const [searchQuery, setSearchQuery] = useState('');
  const [userMessage, setUserMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [lightMode, setLightMode] = useState(true);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const [snippetItems, setSnippetItems] = useState([]);
  const [searchError, setSearchError] = useState(null);
  const [aiSummary, setAiSummary] = useState('');

  // View management
  const [currentView, setCurrentView] = useState('search'); // 'search' or 'preview'
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showRightPanel, setShowRightPanel] = useState(false);

  const scrollContainerRef = useRef(null);

  
  // ============================================
  // CHECK SCREEN SIZE FOR MOBILE/TABLET
  // ============================================
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileView(window.innerWidth <= 1024);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // ============================================
  // AUTO SCROLL TO BOTTOM WHEN NEW CONTENT APPEARS
  // ============================================
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }
  }, [hasSearched, isTyping, currentView]);

  // ============================================
  // FORCE SCROLL CONTAINER TO UPDATE ON RESIZE AND INITIAL LOAD
  // ============================================
  useEffect(() => {
    const handleResize = () => {
      if (scrollContainerRef.current) {
        // Force a reflow to recalculate heights
        scrollContainerRef.current.style.display = 'none';
        setTimeout(() => {
          if (scrollContainerRef.current) {
            scrollContainerRef.current.style.display = 'flex';
          }
        }, 10);
      }
    };

    window.addEventListener('resize', handleResize);
    
    // Initial fix after mount - with a slightly longer delay
    setTimeout(handleResize, 200);
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ============================================
  // HANDLER FUNCTIONS
  // ============================================
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    const user_query = searchQuery.trim();
    const search_id = `search-${Date.now()}`;
    setUserMessage(user_query);
    setHasSearched(true);
    setIsTyping(true);
    setSearchError(null);
    setSnippetItems([]);
    setAiSummary('');

    if (onSearch) {
      onSearch(user_query);
    }

    setSearchQuery('');
    setCurrentView('search');
    setShowRightPanel(false);

    try {
      const user_id = localStorage.getItem('userId') || '1';
      const res = await fetch(`${API_BASE_URL}/v1/api/tea/search?search_id=${search_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id, user_query }),
      });

      if (!res.ok) throw new Error(`Search failed: ${res.status}`);
      const data = await res.json();

      // Extract AI summary if present
      if (data.ai_summary) setAiSummary(data.ai_summary);

      // Normalise results array — API returns { context: [...], ai_summary: '...' }
      const raw = Array.isArray(data) ? data
        : Array.isArray(data.context) ? data.context
        : Array.isArray(data.results) ? data.results
        : Array.isArray(data.data) ? data.data
        : [];

      const mapped = raw.map((item, idx) => ({
        id: idx + 1,
        number: String(idx + 1),
        documentId: item.document_name || item.document_id || item.documentId || item.source || `Result ${idx + 1}`,
        title: item.document_name || item.title || `Result ${idx + 1}`,
        matches: item.matches != null ? String(item.matches) : '1',
        snippet: item.content || item.snippet || item.text || '',
        page: item.page_number != null ? String(item.page_number) : item.page || '—',
        fileUrl: item.source && item.source.startsWith('http') ? item.source : null,
        figImages: Array.isArray(item.fig_url) ? item.fig_url.filter(Boolean)
          : item.fig_url ? [item.fig_url] : [],
        content: {
          title: item.document_name || item.title || `Result ${idx + 1}`,
          introduction: item.content || item.snippet || item.text || '',
          objectives: [],
          scope: '',
          protocols: [],
          monitoring: '',
        },
      }));

      setSnippetItems(mapped);
    } catch (err) {
      console.error('TEA search error:', err);
      setSearchError('Search failed. Please try again.');
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch(e);
    }
  };

  // Handle snippet card click with mobile/desktop logic
  const handleSnippetClick = (snippet) => {
    const snippetDocument = {
      id: `snippet-${snippet.id}`,
      title: snippet.content?.title || snippet.documentId,
      introduction: snippet.content?.introduction || snippet.snippet || 'Document content preview...',
      objectives: snippet.content?.objectives || [],
      scope: snippet.content?.scope || '',
      protocols: snippet.content?.protocols || [],
      monitoring: snippet.content?.monitoring || '',
      fileUrl: snippet.fileUrl || null,
      figImages: snippet.figImages || [],
    };
    
    setSelectedDocument(snippetDocument);
    
    if (isMobileView) {
      // On mobile/tablet, show modal
      setShowMobileModal(true);
    } else {
      // On desktop, show right panel
      setCurrentView('preview');
      setShowRightPanel(true);
    }
  };

  const handleDownload = (docTitle) => {
    alert(`Downloading ${docTitle}...`);
  };

  const closeRightPanel = () => {
    setShowRightPanel(false);
    setSelectedDocument(null);
  };

  const closeMobileModal = () => {
    setShowMobileModal(false);
    setSelectedDocument(null);
  };

  // ============================================
  // MOBILE MODAL COMPONENT
  // ============================================
  const MobileDocumentModal = () => {
    if (!selectedDocument) return null;
    
    return (
      <div className="mobile-modal-overlay" onClick={closeMobileModal}>
        <div className="mobile-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="mobile-modal-header">
            <h3>{selectedDocument.title}</h3>
            <div className="mobile-modal-actions">
              <button className="mobile-modal-btn download" onClick={() => handleDownload(selectedDocument.title)}>
                <Download size={18} />
              </button>
              <button className="mobile-modal-btn close" onClick={closeMobileModal}>
                <X size={18} />
              </button>
            </div>
          </div>
          
          <div className="mobile-modal-body">
            {selectedDocument.fileUrl ? (
              <div className="pdf-iframe-container">
                <iframe
                  src={selectedDocument.fileUrl}
                  title={selectedDocument.title}
                  className="pdf-iframe"
                />
              </div>
            ) : (
              <div className="pdf-no-preview">
                <FileText size={40} className="pdf-no-preview-icon" />
                <p className="pdf-no-preview-title">No preview available</p>
                {selectedDocument.introduction && (
                  <div className="pdf-snippet-content">
                    <p>{selectedDocument.introduction}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ============================================
  // RENDER SEARCH RESULTS VIEW
  // ============================================
  const renderSearchResultsView = () => {
    return (
      <div className="search-results-view">
        {/* Persistent Search Bar at the Top */}
        <div className="results-search-bar">
          <form className="results-search-form" onSubmit={handleSearch}>
            <div className="results-search-container">
              <Search size={20} className="results-search-icon" />
              <input
                type="text"
                className="results-search-input"
                placeholder="Search for documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <button type="submit" className="results-search-btn">
                <Search size={18} />
              </button>
            </div>
          </form>
        </div>

        {/* Current search query display */}
        <div className="current-search-query">
          <span className="current-search-label">Current search:</span>
          <span className="current-search-term">"{userMessage}"</span>
        </div>

        {isTyping ? (
          <div className="typing-indicator">
            <span className="typing-text">Nimi is Searching for the document(s)</span>
            <span className="typing-dots">
              <span className="dot">.</span>
              <span className="dot">.</span>
              <span className="dot">.</span>
            </span>
          </div>
        ) : (
          <>
            {searchError ? (
              <div className="search-error-banner">
                <span>⚠️ {searchError}</span>
              </div>
            ) : (
              <>
                <div className="results-header">
                  <h2 className="found-title">
                    {snippetItems.length > 0
                      ? `Found ${snippetItems.length} result${snippetItems.length !== 1 ? 's' : ''} for "${userMessage}"`
                      : `No results found for "${userMessage}"`}
                  </h2>
                  {aiSummary && (
                    <div className="ai-response ai-summary-markdown">
                      <ReactMarkdown>{aiSummary}</ReactMarkdown>
                    </div>
                  )}
                  {snippetItems.length === 0 && !aiSummary && (
                    <p className="ai-response">Try refining your search terms or using different keywords.</p>
                  )}
                </div>

                {snippetItems.length > 0 && (
                  <>
                    <hr className="divider" />

                    {/* MAIN DOCUMENTS - first 2 results as cards */}
                    <div className="search-documents">
                      {snippetItems.slice(0, 2).map((item) => (
                        <React.Fragment key={item.id}>
                          <div
                            className="document-card-item clickable"
                            onClick={() => handleSnippetClick(item)}
                          >
                            <div className="document-card-left">
                              <div className="slanted-icon-container">
                                <div className="slanted-border"></div>
                                <FileText className="doc-icon" size={22} />
                              </div>
                            </div>
                            <div className="document-card-middle">
                              <span className="card-doc-title">{item.title}</span>
                              {item.page && item.page !== '—' && (
                                <span className="card-doc-pages">Page {item.page}</span>
                              )}
                            </div>
                            <div className="document-card-right">
                              <button
                                className="card-download-action"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownload(item.title);
                                }}
                              >
                                <Download size={14} />
                                <span>Download</span>
                              </button>
                            </div>
                          </div>

                          <div className="feedback-actions">
                            <div className="time-text">
                              {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </div>
                            <div className="action-buttons">
                              <button className="action-btn thumbs-up" title="Helpful">
                                <ThumbsUp size={16} />
                              </button>
                              <button className="action-btn thumbs-down" title="Not helpful">
                                <ThumbsDown size={16} />
                              </button>
                              <button className="action-btn refresh" title="Refresh results">
                                <RefreshCw size={16} />
                              </button>
                            </div>
                          </div>
                        </React.Fragment>
                      ))}
                    </div>

                    {/* SNIPPETS SECTION - remaining results */}
                    {snippetItems.length > 2 && (
                      <div className="snippets-section">
                        <h3 className="snippets-title">Other documents and snippets found</h3>

                        <div className="snippets-list">
                          {snippetItems.slice(2).map((item) => (
                            <div
                              key={item.id}
                              className="snippet-card-item clickable"
                              onClick={() => handleSnippetClick(item)}
                            >
                              <div className="snippet-card-left">
                                <div className="snippet-icon-container">
                                  <div className="snippet-border"></div>
                                  <FileText className="snippet-icon" size={20} />
                                </div>
                              </div>

                              <div className="snippet-card-content">
                                <div className="snippet-header">
                                  <span className="snippet-number">{item.number}.</span>
                                  <span className="snippet-doc-title">{item.documentId}</span>
                                  <span className="snippet-matches">({item.matches})</span>
                                </div>

                                <div className="snippet-text">
                                  <span className="snippet-label">Snippet:</span>
                                  <span className="snippet-excerpt">{item.snippet}</span>
                                </div>

                                {/* Inline fig_url images */}
                                {item.figImages && item.figImages.length > 0 && (
                                  <div className="snippet-images">
                                    {item.figImages.map((src, imgIdx) => (
                                      <img
                                        key={imgIdx}
                                        src={src.startsWith('data:') ? src : `data:image/png;base64,${src}`}
                                        alt={`Figure ${imgIdx + 1}`}
                                        className="snippet-fig-image"
                                      />
                                    ))}
                                  </div>
                                )}

                                <div className="snippet-footer">
                                  <span className="snippet-page">Page {item.page}</span>
                                  <button
                                    className="snippet-view-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSnippetClick(item);
                                    }}
                                  >
                                    View
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    );
  };

  // ============================================
  // RENDER PREVIEW VIEW (Two Column) - DESKTOP ONLY
  // ============================================
  const renderPreviewView = () => {
    // Don't render preview view on mobile/tablet
    if (isMobileView) return null;
    
    return (
      <div className="preview-view">
        <div className="two-column-layout">
          {/* LEFT PANEL - API search results */}
          <div className={`left-panel ${showRightPanel ? 'with-right-panel' : ''}`}>

            {/* Persistent Search Bar */}
            <div className="results-search-bar">
              <form className="results-search-form" onSubmit={handleSearch}>
                <div className="results-search-container">
                  <Search size={20} className="results-search-icon" />
                  <input
                    type="text"
                    className="results-search-input"
                    placeholder="Search for documents..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={handleKeyPress}
                  />
                  <button type="submit" className="results-search-btn">
                    <Send size={18} />
                  </button>
                </div>
              </form>
            </div>

            {/* Current search query */}
            <div className="current-search-query">
              <span className="current-search-label">Current search:</span>
              <span className="current-search-term">"{userMessage}"</span>
            </div>

            <div className="results-header">
              <h2 className="found-title">
                {snippetItems.length > 0
                  ? `Found ${snippetItems.length} result${snippetItems.length !== 1 ? 's' : ''} for "${userMessage}"`
                  : `No results found for "${userMessage}"`}
              </h2>
              {aiSummary && (
                <div className="ai-response ai-summary-markdown">
                  <ReactMarkdown>{aiSummary}</ReactMarkdown>
                </div>
              )}
            </div>

            {snippetItems.length > 0 && (
              <>
                <hr className="divider" />

                {/* First 2 results as document cards */}
                <div className="search-documents">
                  {snippetItems.slice(0, 2).map((item) => (
                    <React.Fragment key={item.id}>
                      <div
                        className="document-card-item clickable"
                        onClick={() => handleSnippetClick(item)}
                      >
                        <div className="document-card-left">
                          <div className="slanted-icon-container">
                            <div className="slanted-border"></div>
                            <FileText className="doc-icon" size={22} />
                          </div>
                        </div>
                        <div className="document-card-middle">
                          <span className="card-doc-title">{item.title}</span>
                          {item.page && item.page !== '—' && (
                            <span className="card-doc-pages">Page {item.page}</span>
                          )}
                        </div>
                        <div className="document-card-right">
                          <button
                            className="card-download-action"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(item.title);
                            }}
                          >
                            <Download size={14} />
                            <span>Download</span>
                          </button>
                        </div>
                      </div>
                      <div className="feedback-actions">
                        <div className="time-text">
                          {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                        <div className="action-buttons">
                          <button className="action-btn thumbs-up" title="Helpful">
                            <ThumbsUp size={16} />
                          </button>
                          <button className="action-btn thumbs-down" title="Not helpful">
                            <ThumbsDown size={16} />
                          </button>
                          <button className="action-btn refresh" title="Refresh results">
                            <RefreshCw size={16} />
                          </button>
                        </div>
                      </div>
                    </React.Fragment>
                  ))}
                </div>

                {/* Remaining results as snippets */}
                {snippetItems.length > 2 && (
                  <div className="snippets-section">
                    <h3 className="snippets-title">Other documents and snippets found</h3>
                    <div className="snippets-list">
                      {snippetItems.slice(2).map((item) => (
                        <div
                          key={item.id}
                          className="snippet-card-item clickable"
                          onClick={() => handleSnippetClick(item)}
                        >
                          <div className="snippet-card-left">
                            <div className="snippet-icon-container">
                              <div className="snippet-border"></div>
                              <FileText className="snippet-icon" size={20} />
                            </div>
                          </div>
                          <div className="snippet-card-content">
                            <div className="snippet-header">
                              <span className="snippet-number">{item.number}.</span>
                              <span className="snippet-doc-title">{item.documentId}</span>
                              <span className="snippet-matches">({item.matches})</span>
                            </div>
                            <div className="snippet-text">
                              <span className="snippet-label">Snippet:</span>
                              <span className="snippet-excerpt">{item.snippet}</span>
                            </div>
                            {item.figImages && item.figImages.length > 0 && (
                              <div className="snippet-images">
                                {item.figImages.map((src, imgIdx) => (
                                  <img
                                    key={imgIdx}
                                    src={src.startsWith('data:') ? src : `data:image/png;base64,${src}`}
                                    alt={`Figure ${imgIdx + 1}`}
                                    className="snippet-fig-image"
                                  />
                                ))}
                              </div>
                            )}
                            <div className="snippet-footer">
                              <span className="snippet-page">Page {item.page}</span>
                              <button
                                className="snippet-view-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSnippetClick(item);
                                }}
                              >
                                View
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* RIGHT PANEL - PDF viewer (DESKTOP ONLY) */}
          {showRightPanel && selectedDocument && (
            <div className="right-panel">
              <div className="right-panel-header">
                <h3>{selectedDocument.title}</h3>
                <div className="right-panel-header-actions">
                  <button className="right-panel-icon-btn download" title="Download" onClick={() => handleDownload(selectedDocument.title)}>
                    <Download size={18} />
                  </button>
                  <button className="right-panel-icon-btn refresh" title="Refresh">
                    <RefreshCw size={18} />
                  </button>
                  <button className="right-panel-icon-btn close" onClick={closeRightPanel} title="Close">
                    <X size={18} />
                  </button>
                </div>
              </div>

              {selectedDocument.fileUrl ? (
                <div className="pdf-iframe-container">
                  <iframe
                    src={selectedDocument.fileUrl}
                    title={selectedDocument.title}
                    className="pdf-iframe"
                  />
                </div>
              ) : (
                <div className="pdf-no-preview">
                  <FileText size={52} className="pdf-no-preview-icon" />
                  <p className="pdf-no-preview-title">No preview available</p>
                  <p className="pdf-no-preview-sub">This document cannot be previewed in the browser.</p>
                  {selectedDocument.introduction && (
                    <div className="pdf-snippet-content">
                      <h4>Excerpt</h4>
                      <p>{selectedDocument.introduction}</p>
                    </div>
                  )}
                  {selectedDocument.figImages && selectedDocument.figImages.length > 0 && (
                    <div className="pdf-fig-images">
                      {selectedDocument.figImages.map((src, i) => (
                        <img
                          key={i}
                          src={src.startsWith('data:') ? src : `data:image/png;base64,${src}`}
                          alt={`Figure ${i + 1}`}
                          className="pdf-fig-image"
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ============================================
  // MAIN RENDER
  // ============================================
  return (
    <div className={`document-search ${lightMode ? 'light-mode' : 'dark-mode'}`}>
      <div className="top-header">
        <div className="header-left">
          <div className="environment-header">
            <button className="back-to-chat-btn" onClick={onBack}>
              <ArrowLeft size={18} />
              <span>Back to Chat</span>
            </button>
            <div className="environment-info">
              <span className="environment-icon">
                <Search size={20} />
              </span>
              <span className="environment-name">Document Search</span>
            </div>
          </div>
        </div>
        <div className="header-right">
          <span className="light-mode-label">Light mode:</span>
          <button className="header-btn" onClick={() => setLightMode(!lightMode)}>
            {lightMode ? <Moon size={18} /> : <Sun size={18} />}
          </button>
        </div>
      </div>

      <div className="mobile-header">
        <button className="mobile-menu-btn" onClick={() => setShowMobileMenu(!showMobileMenu)}>
          <Menu size={20} />
        </button>
        <div className="mobile-title">
          <span className="mode-icon"><Search size={20} /></span>
          <span className="mode-name">Document Search</span>
        </div>
        <button className="mobile-settings-btn">
          <User size={20} />
        </button>
      </div>

      <div className="document-search-main" ref={scrollContainerRef}>
        {!hasSearched ? (
          <div className="welcome-container">
            <h1 className="welcome-title">Document Search</h1>
            <p className="welcome-subtitle">What document are you looking for?</p>
            <form className="search-form" onSubmit={handleSearch}>
              <div className="search-input-container">
                <input
                  type="text"
                  className="search-input"
                  placeholder="What are you looking for?"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
                <button type="submit" className="search-send-btn">
                  <Search size={20} />
                </button>
              </div>
            </form>
          </div>
        ) : (
          <>
            {currentView === 'search' && renderSearchResultsView()}
            {!isMobileView && currentView === 'preview' && renderPreviewView()}
          </>
        )}

        {/* Mobile Modal - Only shows on mobile/tablet */}
        {isMobileView && showMobileModal && <MobileDocumentModal />}

        {showMobileMenu && (
          <div className="mobile-menu-overlay" onClick={() => setShowMobileMenu(false)}>
            <div className="mobile-menu-content" onClick={(e) => e.stopPropagation()}>
              <div className="mobile-menu-header">
                <h3>Menu</h3>
                <button className="close-menu-btn" onClick={() => setShowMobileMenu(false)}>
                  <X size={18} />
                </button>
              </div>
              <div className="mobile-menu-items">
                <button className="mobile-menu-item" onClick={() => { onBack(); setShowMobileMenu(false); }}>
                  <ArrowLeft size={18} /> Back to Chat
                </button>
                <button className="mobile-menu-item" onClick={() => {
                  setHasSearched(false);
                  setUserMessage('');
                  setSearchQuery('');
                  setCurrentView('search');
                  setShowRightPanel(false);
                  setSelectedDocument(null);
                  setShowMobileMenu(false);
                }}>
                  <X size={18} /> Clear Search
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentSearch;